import React, { useMemo } from 'react'
import {
  Calendar,
  Checklist,
  CreditCard,
  Custom,
  Desktop,
  Document,
  NotePad,
  NotificationMessage,
  PeopleTalk,
  ProcessComplete,
  SalesFunnel,
  SentEmail2,
  Setting,
  Store,
  SuccessComplete,
  SuccessMedal,
  System,
  Team,
} from '@workwave-tidal/core/icons/illustrations'
import { ImportUtility } from '@workwave-tidal/lab'
import { SettingHomeProps } from '@user/components/Settings/Home/HomeTidal'
import { launchFormManager } from '@user/components/TopToolbar/Buttons/AppSwitcher/utils'
import { useIcons } from '@user/components/Settings/Home/hooks/useIcons'

export function useData(props: SettingHomeProps) {
  const {
    campaignTrackingEnabled,
    mapViewEnabled,
    d2dEnabled,
    pendingLeadAutoReviewEnabled,
    serviceCodesEnabled,
  } = props
  const { CallIcon } = useIcons()

  const getSettings = useMemo(() => {
    return [
      {
        title: 'Setup',
        subItems: [
          {
            title: 'Company Setup',
            path: '/settings/companySetup',
            icon: <Store size={48} />,
            description:
              'Verify your company details / branch information is correct. This information will be referenced by Sales Center for Sales Teams, notifications, and pre-filling sales agreements.',
          },
          {
            title: 'Roles & Permissions',
            path: '/settings/roles',
            icon: <SuccessMedal size={48} />,
            description:
              'Create a role for each type of user access you would like to enforce. You can assign the same role to more than one user.',
          },
          {
            title: 'Sales Teams',
            path: '/settings/salesTeams',
            icon: <Team size={48} />,
            description:
              'Set up Sales Team(s) for employees who will be using Sales Center. Create a hierarchical structure of positions that matches your current organization. Set a default Sales Team for each branch.',
          },
          {
            title: 'Campaign Tracking',
            path: '/settings/campaignTracking',
            icon: <SentEmail2 size={48} />,
            description:
              'Campaign tracking automatically captures and tracks online marketing campaign efforts.',
            hide: !campaignTrackingEnabled,
          },
          {
            title: 'Import Utility',
            path: '/settings/importUtility',
            icon: <ImportUtility size={48} />,
            description: 'Import a list of records directly into Sales Center.',
          },
          {
            title: 'API Settings',
            path: '/settings/apiSettings',
            icon: <System size={48} />,
            description: 'Manage API access and webhooks.',
          },
          {
            title: 'Service Code Settings',
            path: '/settings/serviceCodeSettings',
            icon: <Setting size={48} />,
            description:
              'Designate which service codes are available for use in Sales Center.',
            dataTestId: 'serviceCodeSettingsLnk',
            hide: !serviceCodesEnabled,
          },
        ],
      },
      {
        title: 'Configure',
        subItems: [
          {
            title: 'Users & Access',
            path: '/settings/users',
            icon: <PeopleTalk size={48} />,
            description:
              'Configure which employees will be accessing Sales Center. Set up existing employees (mapped users), or create new employees who can only access Sales Center (unmapped users). Each employee must have a role and a position on a Sales Team to utilize Sales Center.',
          },
          {
            title: 'Sales Funnels',
            path: '/settings/salesFunnels',
            dataTestId: 'salesFunnelLnk',
            icon: <SalesFunnel size={48} />,
            description:
              'Create a Sales Funnel for each sales process with lead statuses and opportunity stages. Designate a default Sales Funnel to have it pre-selected when adding leads and creating sales opportunities.',
          },
          {
            title: 'Lead & Opportunity Form Settings',
            path: '/settings/formSettings',
            icon: <Desktop size={48} />,
            description:
              'Designate which fields are optional/required and hidden/visible when adding leads and opportunities in Sales Center.',
          },
          {
            title: 'Agreements',
            icon: <Document />,
            isClosingRequirement: true,
            description:
              'Set up closing requirements that prevent sales from being closed/won without an agreement and/or enforce a customer signature.',
          },

          {
            title: 'Scheduling',
            icon: <Calendar />,
            isClosingRequirement: true,
            description:
              'Select Scheduling Event Types and set up closing requirements for which events must be completed to close/win sales.',
          },

          {
            title: 'Capture Card',
            icon: <CreditCard />,
            isClosingRequirement: true,
            description:
              'Set up closing requirements to prevent sales from being closed/won without a card on file.',
          },
          {
            title: 'Notes',
            path: '/settings/notes',
            icon: <NotePad size={48} />,
            description: 'Configure defaults and automation for notes.',
            hide: false,
          },
          {
            title: 'Door to Door Sales',
            path: '/settings/d2d',
            icon: CallIcon,
            description:
              'Set up and manage knock statuses and other configurations.',
            hide: !d2dEnabled || !mapViewEnabled,
          },
          {
            title: 'Pending Lead Settings',
            path: '/settings/pendingLeadSettings',
            icon: <ProcessComplete size={48} />,
            description:
              'Automatically review and convert pending leads into leads once required information is captured.',
            hide: !pendingLeadAutoReviewEnabled,
          },
        ],
      },
      {
        title: 'Customize',
        subItems: [
          {
            title: 'Sales Agreement Templates',
            icon: <Document size={48} />,
            onClick: launchFormManager,
            description:
              'Import and set up a fillable PDF sales agreement to be used in Sales Center, or ask a support representative for one of our starter templates.',
          },
          {
            title: 'Notifications',
            path: '/settings/notifications',
            dataTestId: 'notificationsLnk',
            icon: <NotificationMessage size={48} />,
            description:
              'Set up distribution groups for key events to ensure employees are notified via email for the events that are important to them. You can also manage settings for customer agreement notifications.',
          },
          {
            title: 'Custom Fields',
            path: '/settings/customFields',
            icon: <Custom size={48} />,
            hide: false,
            description:
              'Create and manage fields to track custom attributes on leads and opportunities.',
          },
          {
            title: 'Activities',
            path: '/settings/activities',
            hide: false,
            icon: <Checklist size={48} />,
            description:
              'Create custom activities for employees to track key events. Activity history is automatically aggregated and available for reporting.',
          },
          {
            title: 'Assignment Rules',
            path: '/settings/assignmentRules',
            hide: false,
            icon: <SuccessComplete size={48} />,
            description:
              'Create custom rules to automatically assign records to owners based on specified criteria like geographical location.',
          },
        ],
      },
    ]
  }, [
    CallIcon,
    campaignTrackingEnabled,
    d2dEnabled,
    mapViewEnabled,
    pendingLeadAutoReviewEnabled,
    serviceCodesEnabled,
  ])

  return {
    getSettings,
  }
}
